import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { FiFileText, FiTrash2, FiUpload } from 'react-icons/fi';
import api from '../config/api';
import LoadingButton from './LoadingButton';
import Modal from './Modal';
import { appAlert, appConfirm } from '../utils/appDialog';

/**
 * Settings → Sales letterhead templates for quotations & invoices.
 */
export default function SalesLetterheadSettings() {
  const queryClient = useQueryClient();
  const [showModal, setShowModal] = useState(false);
  const [templateForm, setTemplateForm] = useState({
    name: '',
    doc_type: 'both',
    is_default: true,
    letterhead_offset: 160,
    file: null,
  });

  const { data: templates = [], isLoading } = useQuery(['service-sales-templates'], () =>
    api.get('/services/sales/templates').then((r) => r.data)
  );

  const uploadMutation = useMutation(
    async () => {
      const fd = new FormData();
      fd.append('name', templateForm.name.trim());
      fd.append('doc_type', templateForm.doc_type);
      fd.append('is_default', templateForm.is_default ? 'true' : 'false');
      fd.append('letterhead_offset', String(templateForm.letterhead_offset || 160));
      fd.append('file', templateForm.file);
      return api.post('/services/sales/templates', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['service-sales-templates']);
        setShowModal(false);
        setTemplateForm({
          name: '',
          doc_type: 'both',
          is_default: true,
          letterhead_offset: 160,
          file: null,
        });
      },
      onError: (e) => void appAlert(e.response?.data?.error || e.message),
    }
  );

  const deleteMutation = useMutation((id) => api.delete(`/services/sales/templates/${id}`), {
    onSuccess: () => queryClient.invalidateQueries(['service-sales-templates']),
    onError: (e) => void appAlert(e.response?.data?.error || e.message),
  });

  return (
    <div className="settings-section">
      <h2>
        <FiFileText /> Sales letterhead templates
      </h2>
      <p className="form-hint" style={{ marginTop: 0 }}>
        Upload PDF letterheads used when generating customer quotations and invoices from Services →
        Sales. Content is drawn on the first page below the letterhead offset.
      </p>

      <div style={{ marginBottom: '1rem' }}>
        <button type="button" className="btn btn-primary" onClick={() => setShowModal(true)}>
          <FiUpload /> Upload template
        </button>
      </div>

      {isLoading ? (
        <div className="loading">Loading…</div>
      ) : templates.length === 0 ? (
        <p className="form-hint">
          No templates yet. Upload a company letterhead PDF to get started.
        </p>
      ) : (
        <div className="table-scroll">
          <table className="data-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Applies to</th>
                <th>Default</th>
                <th>Offset (pt)</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {templates.map((t) => (
                <tr key={t.id}>
                  <td>{t.name}</td>
                  <td>
                    {t.doc_type === 'both'
                      ? 'Quotations & invoices'
                      : t.doc_type === 'quotation'
                        ? 'Quotations'
                        : 'Invoices'}
                  </td>
                  <td>{t.is_default ? 'Yes' : '—'}</td>
                  <td>{t.letterhead_offset}</td>
                  <td>
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm"
                      aria-label={`Delete template ${t.name}`}
                      title="Delete"
                      onClick={async () => {
                        if (await appConfirm(`Delete template “${t.name}”?`)) {
                          deleteMutation.mutate(t.id);
                        }
                      }}
                    >
                      <FiTrash2 />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal
        open={showModal}
        onClose={() => setShowModal(false)}
        ariaLabel="Upload PDF template"
      >
        <h2>Upload PDF template</h2>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (!templateForm.file) {
              void appAlert('Choose a PDF file');
              return;
            }
            uploadMutation.mutate();
          }}
        >
          <div className="form-group">
            <label>Name</label>
            <input
              value={templateForm.name}
              onChange={(e) => setTemplateForm({ ...templateForm, name: e.target.value })}
              placeholder="Company letterhead"
              required
            />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Applies to</label>
              <select
                value={templateForm.doc_type}
                onChange={(e) => setTemplateForm({ ...templateForm, doc_type: e.target.value })}
              >
                <option value="both">Quotations & invoices</option>
                <option value="quotation">Quotations only</option>
                <option value="invoice">Invoices only</option>
              </select>
            </div>
            <div className="form-group">
              <label>Letterhead offset (pt)</label>
              <input
                type="number"
                min="40"
                max="400"
                value={templateForm.letterhead_offset}
                onChange={(e) =>
                  setTemplateForm({ ...templateForm, letterhead_offset: e.target.value })
                }
              />
            </div>
          </div>
          <div className="form-group">
            <label>PDF file</label>
            <input
              type="file"
              accept="application/pdf,.pdf"
              onChange={(e) =>
                setTemplateForm({ ...templateForm, file: e.target.files?.[0] || null })
              }
              required
            />
          </div>
          <label className="form-checkbox-label">
            <input
              type="checkbox"
              checked={templateForm.is_default}
              onChange={(e) =>
                setTemplateForm({ ...templateForm, is_default: e.target.checked })
              }
            />
            Set as default template
          </label>
          <div className="modal-actions">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => setShowModal(false)}
            >
              Cancel
            </button>
            <LoadingButton
              type="submit"
              className="btn btn-primary"
              loading={uploadMutation.isLoading}
              icon={FiUpload}
            >
              Upload
            </LoadingButton>
          </div>
        </form>
      </Modal>
    </div>
  );
}
